import { create } from 'zustand';
import { walletService } from '../services/walletService';
import { Wallet, WalletTransaction, RechargeRequest, GetWalletHistoryParams } from '../types';
import { handleApiError, formatErrorForDisplay, logError } from '../lib/errorHandler';
import { addBreadcrumb } from '../lib/sentry';

interface WalletState {
  // State
  wallet: Wallet | null;
  transactions: WalletTransaction[];
  currentPage: number;
  totalPages: number;
  hasMore: boolean;
  isLoading: boolean;
  error: string | null;
  pollingIntervalId: number | null;

  // Actions
  fetchBalance: () => Promise<void>;
  fetchTransactionHistory: (params?: GetWalletHistoryParams) => Promise<void>;
  loadMoreTransactions: () => Promise<void>;
  initiateRecharge: (amount: number, returnUrl?: string) => Promise<{ orderId: string; paymentSessionId: string }>;
  verifyPayment: (orderId: string) => Promise<{ status: string; amount: number; balance: number; walletTransactionId?: string }>;
  startBalancePolling: (interval?: number) => void;
  stopBalancePolling: () => void;
  clearError: () => void;
}

const DEFAULT_BALANCE_POLLING_INTERVAL = 30000; // 30 seconds
const DEFAULT_PAGE_LIMIT = 20;

/**
 * Wallet Store
 * Manages wallet balance and transaction history with polling support
 */
export const useWalletStore = create<WalletState>((set, get) => ({
  // Initial state
  wallet: null,
  transactions: [],
  currentPage: 1,
  totalPages: 1,
  hasMore: false,
  isLoading: false,
  error: null,
  pollingIntervalId: null,

  // Fetch wallet balance
  fetchBalance: async () => {
    // Don't show loading spinner during polling
    const { pollingIntervalId } = get();
    if (!pollingIntervalId) {
      set({ isLoading: true, error: null });
    }

    try {
      const wallet = await walletService.getBalance();

      set({
        wallet,
        isLoading: false,
        error: null,
      });
    } catch (error) {
      const appError = handleApiError(error);
      logError(appError, 'WalletStore.fetchBalance');

      // Don't set error during polling to avoid UI disruption
      if (!get().pollingIntervalId) {
        set({
          isLoading: false,
          error: formatErrorForDisplay(appError),
        });
      }
    }
  },

  // Fetch transaction history
  fetchTransactionHistory: async (params?: GetWalletHistoryParams) => {
    set({ isLoading: true, error: null });

    try {
      const response = await walletService.getHistory({
        page: params?.page || 1,
        limit: params?.limit || DEFAULT_PAGE_LIMIT,
        type: params?.type,
      });

      set({
        transactions: response.transactions,
        currentPage: response.pagination.page,
        totalPages: response.pagination.totalPages,
        hasMore: response.pagination.page < response.pagination.totalPages,
        isLoading: false,
        error: null,
      });
    } catch (error) {
      const appError = handleApiError(error);
      logError(appError, 'WalletStore.fetchTransactionHistory');
      set({
        isLoading: false,
        error: formatErrorForDisplay(appError),
      });
    }
  },

  // Load more transactions (pagination)
  loadMoreTransactions: async () => {
    const { currentPage, hasMore, isLoading } = get();

    if (!hasMore || isLoading) {
      return;
    }

    set({ isLoading: true, error: null });

    try {
      const response = await walletService.getHistory({
        page: currentPage + 1,
        limit: DEFAULT_PAGE_LIMIT,
      });

      set((state) => ({
        transactions: [...state.transactions, ...response.transactions],
        currentPage: response.pagination.page,
        totalPages: response.pagination.totalPages,
        hasMore: response.pagination.page < response.pagination.totalPages,
        isLoading: false,
        error: null,
      }));
    } catch (error) {
      const appError = handleApiError(error);
      logError(appError, 'WalletStore.loadMoreTransactions');
      set({
        isLoading: false,
        error: formatErrorForDisplay(appError),
      });
    }
  },

  // Initiate recharge
  initiateRecharge: async (amount: number, returnUrl?: string) => {
    set({ isLoading: true, error: null });

    try {
      const response = await walletService.initiateRecharge({ amount, returnUrl });

      set({ isLoading: false });
      addBreadcrumb('wallet', 'Recharge initiated', { amount });

      return {
        orderId: response.orderId,
        paymentSessionId: response.paymentSessionId,
      };
    } catch (error) {
      const appError = handleApiError(error);
      logError(appError, 'WalletStore.initiateRecharge');
      set({
        isLoading: false,
        error: formatErrorForDisplay(appError),
      });
      throw error;
    }
  },

  // Verify payment
  verifyPayment: async (orderId: string) => {
    set({ isLoading: true, error: null });

    try {
      const result = await walletService.verifyPayment(orderId);

      addBreadcrumb('wallet', 'Payment verified', { orderId, status: result.status });

      // Update wallet balance if successful
      if (result.status === 'success') {
        set((state) => ({
          wallet: state.wallet
            ? { ...state.wallet, balance: result.balance }
            : {
                id: '',
                userId: '',
                balance: result.balance,
                currency: 'INR',
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString(),
              },
        }));

        // Refresh transaction history to show the new recharge
        await get().fetchTransactionHistory({ page: 1 });
      }

      set({ isLoading: false });
      return result;
    } catch (error) {
      const appError = handleApiError(error);
      logError(appError, 'WalletStore.verifyPayment');
      set({
        isLoading: false,
        error: formatErrorForDisplay(appError),
      });
      throw error;
    }
  },

  // Start balance polling
  startBalancePolling: (interval = DEFAULT_BALANCE_POLLING_INTERVAL) => {
    // Stop existing polling if any
    get().stopBalancePolling();

    console.log('[WalletStore] Starting balance polling');

    // Initial fetch
    get().fetchBalance();

    // Set up interval
    const intervalId = window.setInterval(() => {
      get().fetchBalance();
    }, interval);

    set({ pollingIntervalId: intervalId });
  },

  // Stop balance polling
  stopBalancePolling: () => {
    const { pollingIntervalId } = get();
    if (pollingIntervalId) {
      console.log('[WalletStore] Stopping balance polling');
      window.clearInterval(pollingIntervalId);
      set({ pollingIntervalId: null });
    }
  },

  // Clear error
  clearError: () => {
    set({ error: null });
  },
}));
